/**
 * Admin Route Component
 *
 * Wraps `ProtectedRoute` and additionally requires `user.isAdmin`. Non-admins are sent to
 * the dashboard rather than the login page — they ARE authenticated, they just are not
 * allowed here, and bouncing them to a login form they have already passed is confusing.
 *
 * This is a client-side gate only. It hides the route; it does not protect the data. Every
 * admin-only endpoint behind such a route must independently enforce the same check on the
 * server (see `createRequireAdminMiddleware`).
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';

interface AdminRouteProps {
  children: React.ReactNode;
}

function AdminOnly({ children }: AdminRouteProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function AdminRoute({ children }: AdminRouteProps) {
  return (
    <ProtectedRoute>
      <AdminOnly>{children}</AdminOnly>
    </ProtectedRoute>
  );
}
